import { AxiosError } from 'axios';
import { WorkplaceType } from '../../smallApps/jobs/types/index.types';

export type NullablePartial<T> = {
  [P in keyof T]?: T[P] | null;
};

export type JobPayload = NullablePartial<Job>;

export interface Education {
  academicDegree: string;
  major: string;
}

export type MatchingStatus =
  | 'MATCHING'
  | 'PAUSED_JOB_CAP'
  | 'PAUSED_GLOBAL_CEILING'
  | 'MATCHING_FAILED';

export interface Job {
  id: number;
  employerName: string;
  workplaceTypes: WorkplaceType[];
  externalId: string;
  title: string;
  altJobTitles: string[];
  recruiterName: string | null;
  hiringManagerName: string | null;
  hiringManagerId?: number;
  recruiterId?: number;
  location: {
    /** ISO alpha-2 of the country the job is performed in. Optional on write: blank leaves the stored value. */
    countryCode?: string | null;
    country: string;
    city: string;
    state: string;
    address: string;
    zipCode: string;
    latitude: number;
    longitude: number;
    radius: number;
  };
  educationList: Education[];
  certification: {
    certifications: string[];
    licensures: string[];
  };
  invitationSettings: {
    fitScore: number;
    updatedDays: number;
    distance: number;
  };
  description: string;
  salaryRange: string;
  minSalary: number;
  maxSalary: number;
  requiredSkills: string[];
  preferredSkills: string[];
  minTotalExperience: number;
  maxTotalExperience: number;
  minIndustryExperience: number;
  maxIndustryExperience: number;
  hasManagementExperience: boolean;
  status: string;
  customer: {
    id: number;
    name: string;
    logoUrl: string;
  };
  createdBy: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    customer: {
      id: number;
      name: string;
      logoUrl: string;
    };
    lastLoginAt: string;
    createdAt: string;
    updatedAt: string;
  };
  salaryCurrency: string;
  visaRequirements: string[];
  statusChangedAt: string;
  postedAt: number | string;
  /** Sent on create by a Super Admin, who belongs to no customer. */
  customerId?: number;
  customerIsSynthetic?: boolean;
  /**
   * avrio-backend's matching-limits state (2026-10-02). Absent on jobs saved
   * before matching limits existed, which render exactly as before.
   */
  matchingStatus?: MatchingStatus | null;
  /** Customer-facing, e.g. "Matching paused: monthly limit reached". */
  matchingStatusReason?: string | null;
  matchingStatusUpdatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RioMessage {
  convoId: null | string;
  isFromBot: boolean;
  msgId: string;
  msgTime: number;
  personId: string;
  positionId: number;
  questionId: null | string;
  service: string;
  text: string;
}

export interface Candidate {
  employerName: string;
  fitScore: number;
  /**
   * Rio's interview-adjusted Fit Score, on the candidates LIST (avrio-backend
   * interviewAdjustedFitScore). Present only once an interview produced a usable
   * rescore; absent or null otherwise. SEPARATE from `fitScore`, the matcher's
   * score: the list's sort and its score filter run on `fitScore`, so this is
   * shown beside it as a marker and never in place of it.
   */
  interviewAdjustedFitScore?: number | null;
  id: number;
  jobTitle: string;
  lastActivityAt: number;
  name: string;
  status: string;
  email: string;
  phoneNumber: string;
  updatedAt: number;
  jobId: number;
  skills: string[];
  degrees: Array<{
    academicDegree: string;
    major: string;
    institution: string;
  }>;
  experiences: Array<{
    jobTitle: string;
    employer: string;
    id: number;
    startDate: number;
    endDate: number;
  }>;
  rioMessages: Array<RioMessage>;
  requiredSkills: Array<string>;
  preferredSkills: Array<string>;
  statusChangedAt: number;
  explanatoryNote?: string;
  /**
   * What the interview changed about the pre-interview Fit Score. Absent
   * means the candidate has not completed an interview — matches
   * avrio-backend's own openapi.yaml Rescore schema description verbatim;
   * `fitScore` above stays the resume-derived score either way, this is
   * additive, not a replacement.
   */
  rescore?: {
    scoreBefore: number;
    scoreAfter: number;
    delta: number;
    /** Dimensions the interview did not reach — still resume-only. */
    unresolved: string[];
    changes: Array<{
      dimension: string;
      before: number;
      after: number;
      delta: number;
      source: string;
      /** 0-1. How much the interview finding outweighed the resume prior. Only meaningful when source is "interview". */
      authority: number;
      explanation: string;
    }>;
  };
  dimensions: Record<
    string,
    {
      justification: string;
      score: string;
    }
  >;
}

export interface HiringManager {
  customerId: number;
  customerName: string;
  email: string;
  firstName: string;
  id: number;
  profilePictureUrl: null | string;
  lastName: string;
  role: string;
}

export interface CandidateFeedback {
  createdAt: number;
  hiringManagerName: string;
  profilePictureUrl: string | null;
  text: string;
}

export type InterviewSpeaker = 'RIO' | 'CANDIDATE' | 'UNKNOWN';

export interface InterviewMessage {
  msgId: string;
  speaker: InterviewSpeaker;
  body: string;
  sentAt: string | null;
  questionId: string | null;
}

export interface InterviewConversation {
  convoId: string | null;
  jobId: string;
  startedAt: string | null;
  messages: InterviewMessage[];
}

/** Matches Rio's own status wire values exactly (lower_snake_case) --
 * confirmed against InterviewResultsDynamoService.Status.toWireValue() in
 * avrio-backend, one convention across both services rather than a second
 * casing for the same states. `unknown` is a forward-compatibility escape
 * hatch on the backend's own read path, not a real interview state; callers
 * should treat it the same as `not_started`. */
export type InterviewStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'unknown';

export interface DimensionObservation {
  /** 1-5, the same scale DimensionRow's dots already use -- confirmed
   * against avrio-rio's rescore.py, which documents its own observations
   * shape as `{name: {"score": 1-5, ...}}`. No conversion needed. */
  score: number;
  /** 0-1. */
  confidence: number;
  justification: string;
}

export interface CandidateInterview {
  completed: boolean;
  completedAt: string | null;
  transcript: {
    internalCandidateId: string;
    conversations: InterviewConversation[];
  };
  status: InterviewStatus;
  /** 0-100, the same scale as Candidate.fitScore. Null before the first
   * scored turn. */
  interviewScore: number | null;
  summary: string | null;
  /** ISO-8601. Stamped on every write (create/save-progress/complete), so
   * present even mid-interview -- the basis for the live/stalled 5-minute
   * threshold. */
  updatedAt: string | null;
  dimensionsAssessed: number | null;
  /** True when the interview ended before completion. */
  partial: boolean;
  /** Latest observation per dimension, keyed by the same dimension names
   * Candidate.dimensions uses -- no translation layer. Empty, not absent,
   * when nothing has been assessed yet. */
  observations: Record<string, DimensionObservation>;
  /**
   * LOAD-BEARING, per avrio-rio's own GET /api/interview/result comment:
   * null means the score's scale is safe to compare against
   * Candidate.fitScore; non-null means it is not, and no delta should be
   * shown or computed. Confirmed null on every real record today (the only
   * writer never sends it), but the check exists so nothing needs to change
   * here once that writer starts sending a real value.
   */
  scoringVersion: number | null;
}

export interface Context {
  isLoading: boolean;
  jobList: Array<Job>;
  totalJobElements: number;
  totalCandidateElements: number;
  totalCandidatePages: number;
  totalJobPages: number;
  totalSharedCandidatePages: number;
  totalSharedCandidateElements: number;
  error?: AxiosError;
  jobDetails: Job | null;
  candidates: Candidate[];
  sharedCandidates: Candidate[];
  hiringManagers: HiringManager[];
  candidate?: Candidate;
  candidateFeedback: CandidateFeedback | null;
  candidateDownloadCVUrl: string;
  autoPopulatedValues: Record<
    string,
    Array<string> | number | string | Record<string, unknown>
  >;
}

export enum Status {
  NEW = 'NEW',
  REVIEWED = 'REVIEWED',
  INVITATION_SENT = 'INVITATION_SENT',
  RIO_SCREENING = 'RIO_SCREENING',
  ALT_INTERVIEW = 'ALT_INTERVIEW',
  HM_REVIEW = 'HM_REVIEW',
  OFFERED = 'OFFERED',
  REJECTED = 'REJECTED',
  HIRED = 'HIRED',
  NOT_INTERESTED = 'NOT_INTERESTED',
}

export enum CandidateOfferEventType {
  OFFER_MADE = 'OFFER_MADE',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
}

export enum CandidateOfferType {
  VERBAL = 'VERBAL',
  WRITTEN = 'WRITTEN',
}

/** Mirrors avrio-backend's CandidateOfferEvent (avrio-backend#127/#132/#134). */
export interface CandidateOfferEvent {
  id: number;
  jobId: number;
  candidateId: number;
  eventType: CandidateOfferEventType;
  offerType?: CandidateOfferType;
  compensationSummary?: string;
  proposedStartDate?: string;
  /** Server-recorded timestamp (ISO 8601) - when the backend received the event, not when the browser rendered it. */
  occurredAt: string;
  recordedByUserId: number;
  recordedByName: string;
  recordedByEmail: string;
  recordedByRole: string;
  notes?: string;
}

export enum State {
  INITIAL = 'idle',
  FETCH_JOB_LIST = 'FETCH_JOB_LIST',
  FETCH_JOB_BY_ID = 'FETCH_JOB_BY_ID',
  FETCH_JOB_AUTO_POPULATED_VALUES = 'FETCH_JOB_AUTO_POPULATED_VALUES',
  JOB_ADDED = 'JOB_ADDED',
  JOB_UPDATED = 'JOB_UPDATED',
  EMAIL_SENT = 'EMAIL_SENT',
  CREATE_JOB = 'CREATE_JOB',
  UPDATE_JOB = 'UPDATE_JOB',
  CANDIDATE_SHARED = 'CANDIDATE_SHARED',
  FETCH_CANDIDATES = 'FETCH_CANDIDATES',
  FETCH_SHARED_CANDIDATES = 'FETCH_SHARED_CANDIDATES',
  SEND_CANDIDATES = 'SEND_CANDIDATES',
  SHARE_CANDIDATE = 'SHARE_CANDIDATE',
  FETCH_HIRING_MANAGERS = 'FETCH_HIRING_MANAGERS',
  FETCH_JOB_HIRING_MANAGERS = 'FETCH_JOB_HIRING_MANAGERS',
  ASSIGN_HIRING_MANAGER = 'ASSIGN_HIRING_MANAGER',
  FETCH_RECRUITERS = 'FETCH_RECRUITERS',
  ASSIGN_RECRUITER = 'ASSIGN_RECRUITER',
  GET_CANDIDATE = 'GET_CANDIDATE',
  ADD_CANDIDATE_FEEDBACK = 'ADD_CANDIDATE_FEEDBACK',
  GET_CANDIDATE_FEEDBACK = 'GET_CANDIDATE_FEEDBACK',
  UPDATE_CANDIDATE_STATUS = 'UPDATE_CANDIDATE_STATUS',
  GET_CANDIDATE_DOWNLOAD_CV_URL = 'GET_CANDIDATE_DOWNLOAD_CV_URL',
  ERROR = 'error',
}

export enum EventType {
  FETCH_JOB_LIST = 'FETCH_JOB_LIST',
  FETCH_JOB_BY_ID = 'FETCH_JOB_BY_ID',
  FETCH_JOB_AUTO_POPULATED_VALUES = 'FETCH_JOB_AUTO_POPULATED_VALUES',
  RESET_JOB_AUTO_POPULATED_VALUES = 'RESET_JOB_AUTO_POPULATED_VALUES',
  CREATE_JOB = 'CREATE_JOB',
  UPDATE_JOB = 'UPDATE_JOB',
  FETCH_CANDIDATES = 'FETCH_CANDIDATES',
  FETCH_SHARED_CANDIDATES = 'FETCH_SHARED_CANDIDATES',
  SEND_CANDIDATES = 'SEND_CANDIDATES',
  SHARE_CANDIDATE = 'SHARE_CANDIDATE',
  FETCH_HIRING_MANAGERS = 'FETCH_HIRING_MANAGERS',
  FETCH_JOB_HIRING_MANAGERS = 'FETCH_JOB_HIRING_MANAGERS',
  ASSIGN_HIRING_MANAGER = 'ASSIGN_HIRING_MANAGER',
  FETCH_RECRUITERS = 'FETCH_RECRUITERS',
  ASSIGN_RECRUITER = 'ASSIGN_RECRUITER',
  GET_CANDIDATE = 'GET_CANDIDATE',
  ADD_CANDIDATE_FEEDBACK = 'ADD_CANDIDATE_FEEDBACK',
  GET_CANDIDATE_FEEDBACK = 'GET_CANDIDATE_FEEDBACK',
  UPDATE_CANDIDATE_STATUS = 'UPDATE_CANDIDATE_STATUS',
  GET_CANDIDATE_DOWNLOAD_CV_URL = 'GET_CANDIDATE_DOWNLOAD_CV_URL',
}

export type fetchJobAutopopulatedValuesEvent = {
  type: EventType.FETCH_JOB_AUTO_POPULATED_VALUES;
  payload: { title: string; description: string };
};

export type resetJobAutopopulatedValuesEvent = {
  type: EventType.RESET_JOB_AUTO_POPULATED_VALUES;
};

export type shareCandidateEvent = {
  type: EventType.SHARE_CANDIDATE;
  payload: { jobId: number; candidateId: number; userId: number };
};

export type getCandidateEvent = {
  type: EventType.GET_CANDIDATE;
  payload: { jobId: number; candidateId: number };
};

export type getCandidateDownloadCVUrlEvent = {
  type: EventType.GET_CANDIDATE_DOWNLOAD_CV_URL;
  payload: { jobId: number; candidateId: number };
};

export type updateCandidateStatusEvent = {
  type: EventType.UPDATE_CANDIDATE_STATUS;
  payload: { status: string; jobId: number; candidateId: number };
};


export type addFeedbackEvent = {
  type: EventType.ADD_CANDIDATE_FEEDBACK;
  payload: {
    jobId: number;
    candidateId: number;
    text: string;
    // Debrief fields (RIO-7). The endpoint persists text today; rating and
    // reason are sent so no frontend change is needed when the backend maps
    // them onto hmFeedbackRating and the structured reason. Jackson is
    // configured with FAIL_ON_UNKNOWN_PROPERTIES false, so they are ignored
    // rather than rejected until then.
    rating?: number;
    reason?: string;
  };
};

export type getFeedbackEvent = {
  type: EventType.GET_CANDIDATE_FEEDBACK;
  payload: { jobId: number; candidateId: number };
};

export type sendCandidatesEvent = {
  type: EventType.SEND_CANDIDATES;
  payload: {
    jobId: number;
    candidateIds: React.Key[];
    emails: string[];
    emailBody: string;
  };
};

export type fetchJobHiringmanagersEvent = {
  type: EventType.FETCH_JOB_HIRING_MANAGERS;
  payload: { jobId: number };
};

export type fetchHiringmanagersEvent = {
  type: EventType.FETCH_HIRING_MANAGERS;
};

export type assignHiringmanagersEvent = {
  type: EventType.ASSIGN_HIRING_MANAGER;
  payload: { jobId: number; hiringManagerId: number };
};

export type fetchRecruitersEvent = {
  type: EventType.FETCH_RECRUITERS;
};

export type assignRecruiterEvent = {
  type: EventType.ASSIGN_RECRUITER;
  payload: { jobId: number; recruiterId: number };
};

export type fetchJobListEvent = {
  type: EventType.FETCH_JOB_LIST;
  payload: {
    pageNumber: number;
    pageSize: number;
    sort?: string;
    order?: string;
    status?: string;
    /** Super Admin only: include jobs of customers flagged synthetic (test, E2E, demo). Off by default. */
    includeSynthetic?: boolean;
  };
};

export type fetchCandidatesEvent = {
  type: EventType.FETCH_CANDIDATES;
  payload: {
    jobId: number;
    pageNumber: number;
    pageSize: number;
    fitScore?: number;
    statuses?: Status[];
    hasMandatorySkills?: boolean;
    hasMandatoryTitleMatch?: boolean;
    maxDistance?: number;
    candidatesWithNoLocation?: boolean;
    isApplicant?: boolean;
    sort?: string;
    order?: string;
  };
};

export type fetchSharedCandidatesEvent = {
  type: EventType.FETCH_SHARED_CANDIDATES;
  payload: { pageNumber: number; pageSize: number };
};

export type createJobEvent = {
  type: EventType.CREATE_JOB;
  payload: { job: JobPayload };
};

export type fetchJobByIdEvent = {
  type: EventType.FETCH_JOB_BY_ID;
  payload: { id: number };
};

export type updateJobEvent = {
  type: EventType.UPDATE_JOB;
  payload: { id: number; job: JobPayload };
};

export type Event =
  | fetchJobListEvent
  | createJobEvent
  | fetchJobByIdEvent
  | updateJobEvent
  | fetchCandidatesEvent
  | sendCandidatesEvent
  | fetchHiringmanagersEvent
  | assignHiringmanagersEvent
  | fetchRecruitersEvent
  | assignRecruiterEvent
  | getCandidateEvent
  | addFeedbackEvent
  | getFeedbackEvent
  | fetchJobHiringmanagersEvent
  | fetchSharedCandidatesEvent
  | updateCandidateStatusEvent
  | getCandidateDownloadCVUrlEvent
  | shareCandidateEvent
  | fetchJobAutopopulatedValuesEvent
  | resetJobAutopopulatedValuesEvent;
